import type { components } from "@appretio/api";

export type StackedChartDataPoint =
  components["schemas"]["StackedChartDataPoint"];

const TOP_N = 14;
const OTHER_LABEL = "Other";

function computeDimensionTotals(
  data: StackedChartDataPoint[]
): Map<string, number> {
  const totals = new Map<string, number>();
  for (const d of data) {
    totals.set(
      d.dimension_value,
      (totals.get(d.dimension_value) ?? 0) + Math.abs(d.value)
    );
  }
  return totals;
}

/** Result of grouping stacked chart data into top-N dimensions plus an "Other" bucket. */
export interface GroupedResult {
  data: StackedChartDataPoint[];
  dimensionOrder: string[];
}

/**
 * Groups dimension values by total absolute value, keeps top N,
 * aggregates the rest as "Other". Returns both the filtered data
 * and the dimension order (descending by total, "Other" last).
 */
export function groupTopN(
  data: StackedChartDataPoint[],
  n: number = TOP_N
): GroupedResult {
  const totals = computeDimensionTotals(data);
  const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]);
  const topNames = sorted.slice(0, n).map(([k]) => k);
  const topSet = new Set(topNames);

  if (sorted.length <= n) {
    return { data, dimensionOrder: topNames };
  }

  const otherAgg = new Map<string, number>();
  const result: StackedChartDataPoint[] = [];

  for (const d of data) {
    if (topSet.has(d.dimension_value)) {
      result.push(d);
    } else {
      otherAgg.set(d.time_period, (otherAgg.get(d.time_period) ?? 0) + d.value);
    }
  }

  for (const [time_period, value] of otherAgg) {
    result.push({ time_period, dimension_value: OTHER_LABEL, value });
  }

  return { data: result, dimensionOrder: [...topNames, OTHER_LABEL] };
}

/**
 * Extracts numeric suffix from time period labels for sorting.
 * "W01" → 1, "M12" → 12, "Q3" → 3
 */
function timePeriodSortKey(tp: string): number {
  const num = tp.replace(/\D/g, "");
  return num ? parseInt(num, 10) : 0;
}

/**
 * Returns sorted unique time periods from the data.
 */
export function getSortedTimePeriods(data: StackedChartDataPoint[]): string[] {
  const unique = [...new Set(data.map((d) => d.time_period))];
  return unique.sort((a, b) => timePeriodSortKey(a) - timePeriodSortKey(b));
}

/**
 * Pivots long-format data into AG Charts-ready format:
 * one row per time period, with dimension values as keys.
 *
 * Input:  [{ time_period: "W01", dimension_value: "Nike", value: 100 }, ...]
 * Output: [{ period: "W01", Nike: 100, Adidas: 200 }, ...]
 */
export function pivotForChart(
  data: StackedChartDataPoint[],
  timePeriods: string[]
): Record<string, unknown>[] {
  const map = new Map<string, Record<string, number>>();
  for (const d of data) {
    if (!map.has(d.time_period)) map.set(d.time_period, {});
    const row = map.get(d.time_period)!;
    row[d.dimension_value] = (row[d.dimension_value] ?? 0) + d.value;
  }

  return timePeriods.map((tp) => ({
    period: tp,
    ...(map.get(tp) ?? {})
  }));
}
